import { useState, useRef, useCallback, useEffect, useMemo, memo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useDirectionStore } from '@/store/directionStore'
import { CommandEventsTable } from '@/pages/events/CommandEventsTable'
import { SaasEventsTable } from '@/pages/events/SaasEventsTable'
import { ExpressiveEventsMosaic } from '@/pages/events/ExpressiveEventsMosaic'
import {
  Activity,
  Loader2,
  ImageOff,
  Calendar,
  Camera,
  X,
  ChevronDown,
  Image,
  Video,
  Play,
  Search,
  Trash2,
  MoreVertical,
  CheckSquare,
  Download,
} from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { DateRangePicker } from '@/components/ui/date-range-picker'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { eventsApi, camerasApi, aiModelsApi } from '@/api/endpoints'
import { queryKeys, queryOptions } from '@/config/queryConfig'
import { formatRelativeTime, cn } from '@/lib/utils'
import { getEventDownloads, downloadFile } from '@/lib/eventDownload'
import type { Event, Camera as CameraType, VisualDetection } from '@/types'
import { EventDetailModal } from '@/components/events/EventDetailModal'
import { CameraTelemetryPills } from '@/components/cameras/CameraTelemetryPills'

// Media type badge shown on the thumbnail
function MediaTypeBadge({ mediaType }: { mediaType: 'video' | 'image' }) {
  const { t } = useTranslation('events')
  return (
    <div className="absolute top-2 left-2 flex items-center gap-1 rounded-full bg-black/60 px-2 py-0.5 text-[10px] font-medium text-white backdrop-blur-sm">
      {mediaType === 'video' ? (
        <>
          <Video className="h-3 w-3" />
          {t('types.video')}
        </>
      ) : (
        <>
          <Image className="h-3 w-3" />
          {t('types.photo')}
        </>
      )}
    </div>
  )
}

// Event media component — shows video player if available, otherwise thumbnail image
function EventMedia({
  thumbnailUrl,
  videoUrl,
  mediaType,
  alt,
  showVideo,
  setShowVideo,
}: {
  thumbnailUrl: string | null
  videoUrl: string | null
  mediaType: 'video' | 'image'
  alt: string
  showVideo: boolean
  setShowVideo: (v: boolean) => void
}) {
  const { t } = useTranslation('events')
  const [imageStatus, setImageStatus] = useState<'loading' | 'loaded' | 'error'>(
    thumbnailUrl ? 'loading' : 'error'
  )

  useEffect(() => {
    setImageStatus(thumbnailUrl ? 'loading' : 'error')
  }, [thumbnailUrl])

  const isVideo = mediaType === 'video'

  return (
    <div className="relative w-full aspect-video bg-muted overflow-hidden">
      {showVideo && videoUrl ? (
        <video
          src={videoUrl}
          controls
          autoPlay
          preload="auto"
          className="h-full w-full object-contain bg-black"
          onEnded={() => setShowVideo(false)}
        />
      ) : (
        <>
          {thumbnailUrl && imageStatus !== 'error' ? (
            <>
              {imageStatus === 'loading' && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground/50" />
                </div>
              )}
              <img
                src={thumbnailUrl}
                alt={alt}
                className={`h-full w-full object-cover transition-opacity duration-300 ${
                  imageStatus === 'loaded' ? 'opacity-100' : 'opacity-0'
                }`}
                onLoad={() => setImageStatus('loaded')}
                onError={() => setImageStatus('error')}
              />
            </>
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-1">
              <ImageOff className="h-8 w-8 text-muted-foreground/30" />
              <span className="text-xs text-muted-foreground/50">{t('card.noImage')}</span>
            </div>
          )}

          {/* Always preload video metadata for instant playback */}
          {isVideo && videoUrl && (
            <video src={videoUrl} preload="metadata" className="hidden" />
          )}

          {/* Play button overlay */}
          {isVideo && videoUrl && imageStatus === 'loaded' && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                setShowVideo(true)
              }}
              className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 hover:opacity-100 transition-opacity duration-200"
              aria-label={t('card.playVideo')}
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/90 shadow-lg">
                <Play className="h-5 w-5 text-gray-900 ml-0.5" />
              </div>
            </button>
          )}
        </>
      )}

      {/* Media type badge */}
      <MediaTypeBadge mediaType={mediaType} />
    </div>
  )
}

// DetectionChip — imported from shared UI component
import { DetectionChip } from '@/components/ui/detection-chip'

// Object + audio detection chips
function DetectionChips({ event }: { event: Event }) {
  const { t } = useTranslation('events')
  const detectionMap = useMemo(() => {
    const detections = (event.metadata?.detections as VisualDetection[]) ?? []
    // Max confidence per class across all frames — the best detection of each class,
    // not the arbitrary last-frame value. (#095 follow-up)
    const map: Record<string, number> = {}
    for (const d of detections) map[d.class_name] = Math.max(map[d.class_name] ?? 0, d.confidence)
    return map
  }, [event.metadata])

  const objectClasses = event.detected_objects ?? []
  const audioDetections = event.audio_detections ?? []

  if (!objectClasses.length && !audioDetections.length) return null

  return (
    <div className="mt-2 space-y-1.5">
      {objectClasses.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {objectClasses.map((cls) => (
            <DetectionChip
              key={cls}
              cls={cls}
              confidence={detectionMap[cls]}
              type="object"
            />
          ))}
        </div>
      )}
      {audioDetections.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[9px] uppercase tracking-wide text-muted-foreground/50 font-semibold pr-0.5 select-none">
            {t('card.audio')}
          </span>
          {audioDetections.map((d) => (
            <DetectionChip
              key={d.class_name}
              cls={d.class_name}
              confidence={d.confidence}
              type="audio"
            />
          ))}
        </div>
      )}
    </div>
  )
}

// Event card component
const EventCard = memo(function EventCard({
  event,
  index,
  onEventClick,
  selectionMode,
  selected,
  onToggleSelect,
  onDeleteRequest,
}: {
  event: Event
  index: number
  onEventClick: (index: number) => void
  selectionMode: boolean
  selected: boolean
  onToggleSelect: (id: number) => void
  onDeleteRequest: (event: Event) => void
}) {
  const { t } = useTranslation('events')
  const { t: tc } = useTranslation('common')
  const eventTypeDisplay = tc(`eventTypes.${event.event_type}`, { defaultValue: event.event_type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()) })

  const mediaType = event.media_details?.media_type ?? 'image'

  const { original, annotated } = getEventDownloads(event)

  const [showVideo, setShowVideo] = useState(false)

  const handleCardClick = () => {
    if (selectionMode) {
      onToggleSelect(event.id)
      return
    }
    setShowVideo(false)
    onEventClick(index)
  }

  return (
    <Card
      className={cn(
        'overflow-hidden cursor-pointer transition-all duration-150 flex flex-col relative',
        selectionMode
          ? selected
            ? 'ring-2 ring-primary shadow-md'
            : 'hover:ring-1 hover:ring-primary/40'
          : 'hover:ring-1 hover:ring-primary/30 hover:shadow-sm',
      )}
      onClick={handleCardClick}
    >
      {/* Selection checkbox overlay */}
      {selectionMode && (
        <div className="absolute top-2 right-2 z-10">
          <div
            className={cn(
              'h-5 w-5 rounded border-2 flex items-center justify-center',
              selected
                ? 'bg-primary border-primary'
                : 'bg-background/80 border-muted-foreground/50 backdrop-blur-sm',
            )}
          >
            {selected && <X className="h-3 w-3 text-primary-foreground" strokeWidth={3} />}
          </div>
        </div>
      )}

      {/* Quick download (annotated) — over the thumbnail, hidden during selection */}
      {!selectionMode && annotated && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); downloadFile(annotated.url, annotated.filename) }}
          aria-label={t('card.download')}
          title={t('card.download')}
          className="absolute top-2 right-2 z-10 rounded-full bg-black/45 p-1.5 text-white opacity-70 backdrop-blur-sm transition hover:bg-black/70 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-white/70"
        >
          <Download className="h-4 w-4" />
        </button>
      )}

      <EventMedia
        thumbnailUrl={event.thumbnail_url}
        videoUrl={event.detection_video_url}
        mediaType={mediaType}
        alt={event.event_type}
        showVideo={showVideo}
        setShowVideo={setShowVideo}
      />
      <div className="flex-1 flex flex-col p-3 sm:p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <h3 className="font-semibold text-sm sm:text-base truncate">{eventTypeDisplay}</h3>
              <span
                className={cn(
                  'det-conf-badge whitespace-nowrap shrink-0',
                  event.confidence_score >= 0.8
                    ? 'det-conf-high'
                    : event.confidence_score >= 0.5
                    ? 'det-conf-med'
                    : 'det-conf-low'
                )}
              >
                {Math.round(event.confidence_score * 100)}%
              </span>
            </div>
            <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
              <Camera className="h-3 w-3 flex-shrink-0" />
              <span className="truncate">{event.camera_name}</span>
            </p>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {/* Three-dot menu — only visible when not in selection mode */}
            {!selectionMode && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground hover:text-foreground"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MoreVertical className="h-4 w-4" />
                    <span className="sr-only">{t('card.eventOptions')}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                  {original && (
                    <DropdownMenuItem
                      onClick={(e) => { e.stopPropagation(); downloadFile(original.url, original.filename) }}
                    >
                      <Download className="mr-2 h-4 w-4" />
                      {t('detail.downloadOriginal')}
                    </DropdownMenuItem>
                  )}
                  {annotated && (
                    <DropdownMenuItem
                      onClick={(e) => { e.stopPropagation(); downloadFile(annotated.url, annotated.filename) }}
                    >
                      <Download className="mr-2 h-4 w-4" />
                      {t('detail.downloadAnnotated')}
                    </DropdownMenuItem>
                  )}
                  {(original || annotated) && <DropdownMenuSeparator />}
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive focus:bg-destructive/10"
                    onClick={(e) => {
                      e.stopPropagation()
                      onDeleteRequest(event)
                    }}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    {t('card.deleteEvent')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>

        <div className="flex-1">
          <DetectionChips event={event} />
        </div>

        <div className="flex items-center gap-2 mt-auto pt-2">
          <span className="text-[10px] sm:text-xs text-muted-foreground flex items-center gap-1 min-w-0">
            <Calendar className="h-3 w-3 flex-shrink-0" />
            <span className="truncate">{formatRelativeTime(event.occurred_at)}</span>
          </span>
        </div>
      </div>
    </Card>
  )
})

// Loading skeleton
function EventCardSkeleton() {
  return (
    <Card className="overflow-hidden">
      <div className="w-full aspect-video shimmer" />
      <div className="p-3 sm:p-4 space-y-3">
        <div className="flex justify-between gap-2">
          <div className="shimmer h-4 w-32 rounded" />
          <div className="shimmer h-5 w-10 rounded-full" />
        </div>
        <div className="shimmer h-3 w-24 rounded" />
        <div className="shimmer h-3 w-full rounded" />
        <div className="shimmer h-3 w-3/4 rounded" />
        <div className="flex justify-between gap-2 pt-2">
          <div className="shimmer h-3 w-20 rounded" />
          <div className="shimmer h-7 w-16 rounded" />
        </div>
      </div>
    </Card>
  )
}

// Camera filter dropdown
function CameraFilter({
  cameras,
  selectedCameraId,
  onSelect,
}: {
  cameras: CameraType[]
  selectedCameraId: number | null
  onSelect: (id: number | null) => void
}) {
  const { t } = useTranslation('events')
  const [search, setSearch] = useState('')
  const selectedCamera = cameras.find((c) => c.id === selectedCameraId)
  const filtered = search.trim()
    ? cameras.filter((c) => c.name.toLowerCase().includes(search.toLowerCase()))
    : cameras

  return (
    <DropdownMenu onOpenChange={(open) => { if (!open) setSearch('') }}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="w-full lg:w-[220px] justify-between">
          <span className="flex items-center gap-2 min-w-0">
            <Camera className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">{selectedCamera ? selectedCamera.name : t('filters.allCameras')}</span>
          </span>
          <ChevronDown className="h-4 w-4 opacity-50 flex-shrink-0 ml-2" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[260px] p-0">
        <div className="flex items-center gap-2 border-b px-3 py-2">
          <Search className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
          <input
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            placeholder={t('filters.searchCameras')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
          {search && (
            <button onClick={() => setSearch('')} className="text-muted-foreground hover:text-foreground">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <div className="max-h-64 overflow-y-auto py-1">
          <DropdownMenuItem onClick={() => onSelect(null)}>
            <span className={cn('text-sm', !selectedCameraId && 'font-medium')}>{t('filters.allCameras')}</span>
          </DropdownMenuItem>
          {filtered.length === 0 && (
            <p className="px-3 py-2 text-xs text-muted-foreground">{t('filters.noCamerasMatch')}</p>
          )}
          {filtered.map((camera) => (
            <DropdownMenuItem key={camera.id} onClick={() => onSelect(camera.id)}>
              <span className={cn('text-sm truncate', selectedCameraId === camera.id && 'font-medium text-primary')}>
                {camera.name}
              </span>
            </DropdownMenuItem>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export function EventsPage() {
  const { t } = useTranslation('events')
  const { t: tc } = useTranslation('common')
  const direction = useDirectionStore((s) => s.direction)
  const [searchParams, setSearchParams] = useSearchParams()
  const queryClient = useQueryClient()

  // Filter state from URL params
  const [selectedCameraId, setSelectedCameraId] = useState<number | null>(
    searchParams.get('camera') ? parseInt(searchParams.get('camera')!, 10) : null
  )
  const [eventTypeFilter, setEventTypeFilter] = useState<string>(
    searchParams.get('event_type') ?? 'all'
  )
  const [mediaTypeFilter, setMediaTypeFilter] = useState<string>(
    searchParams.get('media_type') ?? 'all'
  )
  const [detectedObjectFilter, setDetectedObjectFilter] = useState<string>(
    searchParams.get('detected_object') ?? 'all'
  )
  const [dateRange, setDateRange] = useState({
    start: searchParams.get('start_date') ?? '',
    end: searchParams.get('end_date') ?? '',
  })

  // Lightbox state
  const [selectedEventIndex, setSelectedEventIndex] = useState<number | null>(null)

  // Notification-click open flow (#078).
  // Holds the event id from `?openEvent=` while we clear filters and locate the event.
  const [pendingOpenEventId, setPendingOpenEventId] = useState<number | null>(null)
  // Holds a fetched-by-id event when it's not in the current page-1 list, so we can render the modal on it.
  const [openEventFallback, setOpenEventFallback] = useState<Event | null>(null)
  // #078 addendum: per-event fresh overrides for video events whose detection_video_url
  // wasn't ready when the WS-triggered events-list refetch landed. Keyed by event id.
  const [eventOverrides, setEventOverrides] = useState<Map<number, Event>>(new Map())

  // Fire-and-forget refresh for a video event that may still be processing.
  // If the fetched copy has detection_video_url, stash it as an override so the
  // modal (and any other consumer of composedEvents) renders the playable video.
  const ensureFreshVideo = useCallback((event: Event | undefined) => {
    if (!event) return
    if (event.media_details?.media_type !== 'video') return
    if (event.detection_video_url) return
    eventsApi.getById(event.id)
      .then((fresh) => {
        if (fresh.detection_video_url) {
          setEventOverrides((prev) => {
            const next = new Map(prev)
            next.set(event.id, fresh)
            return next
          })
        }
      })
      .catch(() => {
        // Stay on the thumbnail; user can refresh later if processing failed.
      })
  }, [])

  // Consume `?openEvent=<id>` from the URL: stage the id, then clear filters.
  // `clearFilters()` already wipes the URL via `setSearchParams({})`, which strips this param too.
  useEffect(() => {
    const raw = searchParams.get('openEvent')
    if (!raw) return
    const id = parseInt(raw, 10)
    if (Number.isNaN(id)) return
    setPendingOpenEventId(id)
    clearFilters()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  // Selection / bulk delete state
  const [selectionMode, setSelectionMode] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())

  // Confirm dialog state
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [eventToDelete, setEventToDelete] = useState<Event | null>(null) // single delete
  const [bulkDeletePending, setBulkDeletePending] = useState(false)      // bulk delete

  // Infinite scroll ref
  const loadMoreRef = useRef<HTMLDivElement>(null)

  // Fetch cameras for filter dropdown
  const { data: cameras = [] } = useQuery({
    queryKey: queryKeys.cameras.all(),
    queryFn: () => camerasApi.getAll(),
    ...queryOptions.cameras.all,
  })

  const { data: detectionClasses = [] } = useQuery({
    queryKey: queryKeys.detectionClasses(),
    queryFn: () => aiModelsApi.getDetectionClasses(),
    ...queryOptions.detectionClasses,
  })

  // Sync filter changes to URL params
  const updateSearchParams = (updates: Record<string, string | null>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      for (const [key, value] of Object.entries(updates)) {
        if (value === null || value === '' || value === 'all') {
          next.delete(key)
        } else {
          next.set(key, value)
        }
      }
      return next
    })
  }

  const handleCameraSelect = (cameraId: number | null) => {
    setSelectedCameraId(cameraId)
    updateSearchParams({ camera: cameraId ? cameraId.toString() : null })
  }

  const handleEventTypeChange = (value: string) => {
    setEventTypeFilter(value)
    updateSearchParams({ event_type: value })
  }

  const handleMediaTypeChange = (value: string) => {
    setMediaTypeFilter(value)
    updateSearchParams({ media_type: value })
  }

  const handleDetectedObjectChange = (value: string) => {
    setDetectedObjectFilter(value)
    updateSearchParams({ detected_object: value })
  }

  const handleDateRangeChange = (range: { start: string; end: string }) => {
    setDateRange(range)
    updateSearchParams({ start_date: range.start, end_date: range.end })
  }

  const clearFilters = () => {
    setSelectedCameraId(null)
    setEventTypeFilter('all')
    setMediaTypeFilter('all')
    setDetectedObjectFilter('all')
    setDateRange({ start: '', end: '' })
    setSearchParams({})
  }

  const hasActiveFilters =
    selectedCameraId !== null ||
    eventTypeFilter !== 'all' ||
    mediaTypeFilter !== 'all' ||
    detectedObjectFilter !== 'all' ||
    dateRange.start !== '' ||
    dateRange.end !== ''

  // Build API filters
  const filters = {
    ...(selectedCameraId && { camera: selectedCameraId }),
    ...(eventTypeFilter !== 'all' && { event_type: eventTypeFilter as import('@/types').EventType }),
    ...(mediaTypeFilter !== 'all' && { media_type: mediaTypeFilter as 'video' | 'image' }),
    ...(detectedObjectFilter !== 'all' && { detected_object: detectedObjectFilter }),
    ...(dateRange.start && { start_time: `${dateRange.start}T00:00:00` }),
    ...(dateRange.end && { end_time: `${dateRange.end}T23:59:59` }),
  }

  // Infinite query
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error,
  } = useInfiniteQuery({
    queryKey: queryKeys.events.paginated(filters),
    queryFn: ({ pageParam = 1 }) => eventsApi.getAll(filters, pageParam),
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.next) {
        return allPages.length + 1
      }
      return undefined
    },
    initialPageParam: 1,
    ...queryOptions.events.paginated,
  })

  const events = data?.pages.flatMap((page) => page.results) ?? []
  const totalCount = data?.pages[0]?.count ?? 0
  const selectedCamera = cameras.find((c) => c.id === selectedCameraId)

  // #078: events the modal renders — overlay any fresh-fetched overrides on the cached list
  // (and prepend openEventFallback for the fetch-by-id path).
  const composedEvents = useMemo(() => {
    const base = openEventFallback ? [openEventFallback, ...events] : events
    if (eventOverrides.size === 0) return base
    return base.map((e) => eventOverrides.get(e.id) ?? e)
  }, [events, openEventFallback, eventOverrides])

  // Hold the latest composedEvents in a ref so click/nav handlers can stay stable.
  // `events` is a fresh array on every render (data.pages.flatMap), so without this the
  // handler deps would churn and break EventCard's React.memo for every visible card.
  const composedEventsRef = useRef(composedEvents)
  composedEventsRef.current = composedEvents

  // Click handler for EventCard. Open the modal immediately on the cached row; if it's a
  // video without a finalized URL, kick off a background refetch that will swap in the
  // playable video once processing completes (#078 addendum).
  const handleEventClick = useCallback((index: number) => {
    setSelectedEventIndex(index)
    ensureFreshVideo(composedEventsRef.current[index])
  }, [ensureFreshVideo])

  // Prev/next navigation inside the modal — same refresh-on-video pattern as a fresh click.
  const handleModalNavigate = useCallback((idx: number) => {
    setSelectedEventIndex(idx)
    ensureFreshVideo(composedEventsRef.current[idx])
  }, [ensureFreshVideo])

  // Modal close — clear selection state and the fallback (overrides persist intentionally
  // so re-clicking the same event doesn't trigger a redundant getById).
  const handleModalClose = useCallback(() => {
    setSelectedEventIndex(null)
    setOpenEventFallback(null)
  }, [])

  // #078: when the pending event id is set, and filters are clear, and the query has loaded,
  // try to find the event in the current list; otherwise fall back to a direct fetch.
  useEffect(() => {
    if (pendingOpenEventId === null) return
    if (hasActiveFilters) return  // wait for clearFilters() to flush
    if (isLoading) return         // wait for the unfiltered query to load

    const idx = events.findIndex((e) => e.id === pendingOpenEventId)
    if (idx >= 0) {
      setSelectedEventIndex(idx)
      setPendingOpenEventId(null)
      ensureFreshVideo(events[idx])  // swap to playable video if it just finished processing
      return
    }

    // Not in page 1 — fall back to fetch-by-id.
    const fetchId = pendingOpenEventId
    setPendingOpenEventId(null)  // clear first so we don't re-enter this effect
    eventsApi.getById(fetchId)
      .then((event) => {
        setOpenEventFallback(event)
        setSelectedEventIndex(0)
        // Fallback fetch is already fresh; no extra refetch needed.
      })
      .catch(() => {
        toast.error(t('peek.eventNoLongerAvailable', { ns: 'notifications' }))
      })
  }, [pendingOpenEventId, events, isLoading, hasActiveFilters, t, ensureFreshVideo])

  // Intersection Observer for infinite scroll
  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [target] = entries
      if (target.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage()
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  )

  useEffect(() => {
    const element = loadMoreRef.current
    if (!element) return
    const observer = new IntersectionObserver(handleObserver, {
      threshold: 0.1,
      rootMargin: '100px',
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [handleObserver])

  const activeFilterCount = [
    selectedCameraId !== null,
    eventTypeFilter !== 'all',
    mediaTypeFilter !== 'all',
    detectedObjectFilter !== 'all',
    dateRange.start !== '' || dateRange.end !== '',
  ].filter(Boolean).length

  // ── Delete mutations ──────────────────────────────────────────────────────

  const invalidateEvents = () => {
    queryClient.invalidateQueries({ queryKey: ['events'] })
  }

  // Single delete
  const deleteMutation = useMutation({
    mutationFn: (id: number) => eventsApi.delete(id),
    onSuccess: () => {
      toast.success(t('messages.deleted'))
      invalidateEvents()
      setConfirmOpen(false)
      setEventToDelete(null)
    },
    onError: () => {
      toast.error(t('messages.deleteFailed'))
    },
  })

  // Bulk delete — sequential deletions
  const bulkDeleteMutation = useMutation({
    // One request instead of N sequential deletes (#097). Server does the full
    // cleanup (S3 + linked media + raw file) and returns a partial result.
    mutationFn: (ids: number[]) => eventsApi.bulkDelete(ids),
    onSuccess: (result) => {
      const deleted = result.deleted?.length ?? 0
      const failed = result.failed?.length ?? 0
      if (failed > 0) {
        toast.warning(t('messages.bulkDeletedPartial', { deleted, failed }))
      } else {
        toast.success(t('messages.bulkDeleted', { count: deleted }))
      }
      setSelectedIds(new Set())
      setSelectionMode(false)
      setConfirmOpen(false)
      setBulkDeletePending(false)
      invalidateEvents()
    },
    onError: () => {
      toast.error(t('messages.bulkDeleteFailed'))
      invalidateEvents()
    },
  })

  // ── Selection helpers ─────────────────────────────────────────────────────

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const exitSelectionMode = () => {
    setSelectionMode(false)
    setSelectedIds(new Set())
  }

  // "Select all" = all currently-loaded events, capped at the server's per-
  // request limit (#097). A whole-history wipe isn't an id-list operation.
  const BULK_DELETE_LIMIT = 500
  const selectAll = () => {
    const ids = events.slice(0, BULK_DELETE_LIMIT).map((e) => e.id)
    setSelectedIds(new Set(ids))
    if (events.length > BULK_DELETE_LIMIT) {
      toast.info(t('selection.selectAllCapped', { count: BULK_DELETE_LIMIT }))
    }
  }
  const allSelected =
    events.length > 0 && selectedIds.size >= Math.min(events.length, BULK_DELETE_LIMIT)

  // ── Confirm dialog helpers ────────────────────────────────────────────────

  const requestDeleteSingle = (event: Event) => {
    setEventToDelete(event)
    setBulkDeletePending(false)
    setConfirmOpen(true)
  }

  const requestDeleteSelected = () => {
    setBulkDeletePending(true)
    setEventToDelete(null)
    setConfirmOpen(true)
  }

  const handleConfirmDelete = () => {
    if (bulkDeletePending) {
      bulkDeleteMutation.mutate(Array.from(selectedIds))
    } else if (eventToDelete) {
      deleteMutation.mutate(eventToDelete.id)
    }
  }

  const isDeleting = deleteMutation.isPending || bulkDeleteMutation.isPending

  const confirmTitle = bulkDeletePending
    ? t('deleteConfirm.titleBulk', { count: selectedIds.size })
    : t('deleteConfirm.title')

  const confirmDescription = bulkDeletePending
    ? t('deleteConfirm.descriptionBulk', { count: selectedIds.size })
    : t('deleteConfirm.descriptionSingle')

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Page Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-2 min-w-0">
          <div>
            <h2 className="text-xl sm:text-2xl font-semibold tracking-tight">
              {selectedCamera ? t('pageTitleCamera', { name: selectedCamera.name }) : t('pageTitle')}
            </h2>
            <p className="text-sm text-muted-foreground">
              {totalCount > 0
                ? t('pageDescriptionCount', { count: totalCount })
                : t('pageDescription')}
            </p>
          </div>
          {/* Telemetry pills — only when a single camera is selected (#038) */}
          {selectedCamera && <CameraTelemetryPills camera={selectedCamera} />}
        </div>

        {/* Select mode toggle + Select all */}
        {!isLoading && events.length > 0 && (
          <div className="flex items-center gap-2 flex-shrink-0 mt-1">
            {selectionMode && (
              <Button
                variant="outline"
                size="sm"
                onClick={allSelected ? () => setSelectedIds(new Set()) : selectAll}
              >
                {allSelected ? t('selection.deselectAll') : t('selection.selectAll')}
              </Button>
            )}
            <Button
              variant={selectionMode ? 'secondary' : 'outline'}
              size="sm"
              onClick={selectionMode ? exitSelectionMode : () => setSelectionMode(true)}
            >
              <CheckSquare className="h-4 w-4 mr-2" />
              {selectionMode ? tc('actions.cancel') : t('selection.select')}
            </Button>
          </div>
        )}
      </div>

      {/* Bulk action toolbar — slides in when items are selected */}
      {selectionMode && selectedIds.size > 0 && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-2.5">
          <span className="text-sm font-medium text-destructive">
            {t('selection.selected', { count: selectedIds.size })}
          </span>
          <Button
            variant="destructive"
            size="sm"
            onClick={requestDeleteSelected}
            disabled={isDeleting}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            {t('selection.deleteSelected')}
          </Button>
        </div>
      )}

      {/* Filters */}
      <div className="rounded-lg border border-border/50 bg-muted/30 px-3 py-2.5">
        <div className="flex flex-col lg:flex-row lg:items-center gap-2">
          <CameraFilter
            cameras={cameras.filter((c) => !c.is_locked)}
            selectedCameraId={selectedCameraId}
            onSelect={handleCameraSelect}
          />

          <div className="hidden lg:block h-5 w-px bg-border/60 flex-shrink-0" />

          <Select value={eventTypeFilter} onValueChange={handleEventTypeChange}>
            <SelectTrigger className="w-full lg:w-[160px] bg-background">
              <SelectValue placeholder={t('filters.eventType')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('filters.allEvents')}</SelectItem>
              <SelectItem value="person_detected">{t('filters.personDetected')}</SelectItem>
              <SelectItem value="vehicle_detected">{t('filters.vehicleDetected')}</SelectItem>
              <SelectItem value="animal_detected">{t('filters.animalDetected')}</SelectItem>
              <SelectItem value="motion_detected">{t('filters.motionDetected')}</SelectItem>
            </SelectContent>
          </Select>

          <Select value={mediaTypeFilter} onValueChange={handleMediaTypeChange}>
            <SelectTrigger className="w-full lg:w-[130px] bg-background">
              <SelectValue placeholder={t('filters.mediaType')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('filters.allMedia')}</SelectItem>
              <SelectItem value="video">
                <span className="flex items-center gap-2"><Video className="h-3.5 w-3.5" />{t('filters.videos')}</span>
              </SelectItem>
              <SelectItem value="image">
                <span className="flex items-center gap-2"><Image className="h-3.5 w-3.5" />{t('filters.photos')}</span>
              </SelectItem>
            </SelectContent>
          </Select>

          <Select value={detectedObjectFilter} onValueChange={handleDetectedObjectChange}>
            <SelectTrigger className="w-full lg:w-[155px] bg-background">
              <SelectValue placeholder={t('filters.detectedObject')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('filters.allObjects')}</SelectItem>
              {detectionClasses.map((cls) => (
                <SelectItem key={cls.id} value={cls.class_name}>
                  <span className="capitalize">{tc(`detectionClasses.${cls.class_name}`, { defaultValue: cls.class_name.replace(/_/g, ' ') })}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="hidden lg:block h-5 w-px bg-border/60 flex-shrink-0" />

          <DateRangePicker
            value={dateRange}
            onChange={handleDateRangeChange}
            placeholder={t('filters.dateRange')}
            className="w-full lg:w-[260px]"
            maxDate={new Date()}
          />

          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="w-full lg:w-auto lg:ml-auto flex-shrink-0">
              <X className="h-4 w-4 mr-1" />
              {t('filters.clear')}
              {activeFilterCount > 0 && (
                <span className="ml-1.5 rounded-full bg-background px-1.5 py-0.5 text-[10px] font-medium border border-border/60">
                  {activeFilterCount}
                </span>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Error State */}
      {isError && (
        <Card className="border-destructive">
          <CardContent className="flex h-32 items-center justify-center text-destructive">
            {t('messages.errorLoading', { message: (error as Error)?.message || 'Unknown error' })}
          </CardContent>
        </Card>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <EventCardSkeleton key={i} />
          ))}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && events.length === 0 && (
        <Card>
          <CardContent className="flex h-64 flex-col items-center justify-center text-center p-4">
            <Activity className="mb-4 h-10 w-10 sm:h-12 sm:w-12 text-muted-foreground" />
            <h3 className="text-base sm:text-lg font-semibold">{t('empty.title')}</h3>
            <p className="text-xs sm:text-sm text-muted-foreground mt-2 max-w-md">
              {hasActiveFilters
                ? t('empty.filterMessage')
                : t('empty.message')}
            </p>
            {hasActiveFilters && (
              <Button variant="outline" className="mt-4" onClick={clearFilters}>
                {t('filters.clearFilters')}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Events Grid / Table / Mosaic — direction switch around list rendering only.
          The sentinel (loadMoreRef) stays OUTSIDE the switch so infinite scroll
          works identically in every direction. current = verbatim upstream block. */}
      {!isLoading && events.length > 0 && (
        <>
          {/* ── current: verbatim upstream grid ─────────────────────────── */}
          {direction === 'current' && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              {composedEvents.map((event, index) => (
                <EventCard
                  key={event.id}
                  event={event}
                  index={index}
                  onEventClick={handleEventClick}
                  selectionMode={selectionMode}
                  selected={selectedIds.has(event.id)}
                  onToggleSelect={toggleSelect}
                  onDeleteRequest={requestDeleteSingle}
                />
              ))}
            </div>
          )}

          {/* ── command: dense mono table (§1) ──────────────────────────── */}
          {direction === 'command' && (
            <CommandEventsTable
              events={composedEvents}
              selectionMode={selectionMode}
              selectedIds={selectedIds}
              onEventClick={handleEventClick}
              onToggleSelect={toggleSelect}
              onDeleteRequest={requestDeleteSingle}
            />
          )}

          {/* ── saas: hairline table (§2) ────────────────────────────────── */}
          {direction === 'saas' && (
            <SaasEventsTable
              events={composedEvents}
              selectionMode={selectionMode}
              selectedIds={selectedIds}
              onEventClick={handleEventClick}
              onToggleSelect={toggleSelect}
              onDeleteRequest={requestDeleteSingle}
            />
          )}

          {/* ── expressive: media mosaic (§3) ───────────────────────────── */}
          {direction === 'expressive' && (
            <ExpressiveEventsMosaic
              events={composedEvents}
              selectionMode={selectionMode}
              selectedIds={selectedIds}
              onEventClick={handleEventClick}
              onToggleSelect={toggleSelect}
            />
          )}

          {/* Load More Trigger — OUTSIDE the direction switch so the observer
              attaches identically regardless of which list renderer is active */}
          <div ref={loadMoreRef} className="flex justify-center py-4">
            {isFetchingNextPage && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>{t('messages.loading')}</span>
              </div>
            )}
            {!hasNextPage && events.length > 0 && (
              <p className="text-sm text-muted-foreground">
                {t('messages.showingAll', { count: events.length })}
              </p>
            )}
          </div>
        </>
      )}

      {/* Event detail lightbox */}
      <EventDetailModal
        events={composedEvents}
        selectedIndex={selectedEventIndex}
        onClose={handleModalClose}
        onNavigate={handleModalNavigate}
      />

      {/* Confirm delete dialog */}
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!isDeleting) {
            setConfirmOpen(open)
            if (!open) {
              setEventToDelete(null)
              setBulkDeletePending(false)
            }
          }
        }}
        title={confirmTitle}
        description={confirmDescription}
        confirmLabel={isDeleting ? t('deleteConfirm.deleting') : bulkDeletePending ? t('deleteConfirm.confirmBulk', { count: selectedIds.size }) : t('card.deleteEvent')}
        loading={isDeleting}
        onConfirm={handleConfirmDelete}
      />
    </div>
  )
}
